import React from "react";
import { Doughnut } from "react-chartjs-2";
import PropTypes from "prop-types";

// Import required Chart.js modules
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend as ChartLegend,
} from "chart.js";

// Register Chart.js modules
ChartJS.register(ArcElement, Tooltip, ChartLegend);

const DoughnutChart = ({ dataSet, thickness = 30, legendPosition = 'left', showPercentages = false }) => {
  // Calculate the total cost and format it to 2 decimal places
  const totalCost = dataSet.reduce((sum, item) => sum + item.value, 0).toFixed(2);

  // Calculate total for percentages
  const totalForPercentage = dataSet.reduce((sum, item) => sum + item.value, 0);

  // Chart.js dataset and options
  const chartData = {
    labels: dataSet.map((item) => item.name),
    datasets: [
      {
        data: dataSet.map((item) => parseFloat(item.value.toFixed(2))), // Ensure 2 decimal places
        backgroundColor: dataSet.map((item) => item.color),
        borderWidth: 0,
        cutout: `${100 - thickness}%`, // Controls the thickness of the doughnut
      },
    ],
  };

  const chartOptions = {
    plugins: {
      legend: {
        display: false, // Disable the Chart.js legend
      },
      tooltip: {
        callbacks: {
          label: (tooltipItem) => {
            const datasetItem = dataSet[tooltipItem.dataIndex];
            return `${datasetItem.name}: ${datasetItem.value.toFixed(2)} kr`; // Format tooltip to 2 decimals
          },
        },
      },
    },
    maintainAspectRatio: false,
    responsive: true,
  };

  // Legend component for reuse
  const LegendComponent = () => (
    <div style={{ flexShrink: 0 }}>
      {/* Total Cost */}
      <h2 style={{ marginBottom: "20px", fontSize: "1.5rem", color: "#003D7A", fontWeight: "700" }}>
        {`${parseFloat(totalCost).toLocaleString("sv-SE")} kr`}
      </h2>
      {dataSet.map((item, index) => {
        const percentage = showPercentages
          ? ((item.value / totalForPercentage) * 100).toFixed(0)
          : null;
        return (
          <div
            key={index}
            style={{
              display: "flex",
              alignItems: "center",
              marginBottom: "10px",
            }}
          >
            <div
              style={{
                width: "12px",
                height: "12px",
                backgroundColor: item.color,
                borderRadius: "3px",
                marginRight: "8px",
                flexShrink: 0,
              }}
            ></div>
            <span style={{ fontSize: "14px", wordBreak: "break-word", flex: 1 }}>
              {item.name}
            </span>
            {showPercentages && (
              <span
                style={{
                  fontSize: "13px",
                  fontWeight: "600",
                  color: item.color,
                  marginLeft: "8px",
                }}
              >
                {percentage}%
              </span>
            )}
          </div>
        );
      })}
    </div>
  );

  // Chart component for reuse
  const ChartComponent = () => (
    <div
      style={{
        position: "relative",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flex: 1,
      }}
    >
      <Doughnut
        data={chartData}
        options={{
          ...chartOptions,
          maintainAspectRatio: true,
        }}
      />
    </div>
  );

  // Determine layout based on legendPosition
  const isLegendRight = legendPosition === "right";

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
        height: "100%",
        position: "relative",
        flexDirection: isLegendRight ? "row" : "row",
      }}
    >
      {!isLegendRight && (
        <div style={{ marginRight: "20px" }}>
          <LegendComponent />
        </div>
      )}

      <ChartComponent />

      {isLegendRight && (
        <div style={{ marginLeft: "20px", minWidth: "120px" }}>
          <LegendComponent />
        </div>
      )}
    </div>
  );
};

DoughnutChart.propTypes = {
  dataSet: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
      color: PropTypes.string.isRequired,
    })
  ).isRequired,
  thickness: PropTypes.number,
  legendPosition: PropTypes.oneOf(['left', 'right']),
  showPercentages: PropTypes.bool,
};

DoughnutChart.defaultProps = {
  thickness: 30,
  legendPosition: 'left',
  showPercentages: false,
};

export default DoughnutChart;